import { staffName, type Order, type PaymentMethod, type Transaction } from '../POS/data';
import {
  applyCancel, billNo, flagInvoice, fmtMoney, ymd,
  type Bill, type BillRefund, type BillSource, type KtState, type PayMethod, type Payment, type Service,
} from './ktLogic';

/**
 * Nối POS sang Kế toán: chỉ đọc đơn và giao dịch của POS, không ghi ngược.
 * Đơn hôm nay vào sổ ngay; đơn đổi sau khi đã khóa sổ thì bill giữ nguyên và báo Kế toán.
 */

const METHOD: Record<PaymentMethod, PayMethod> = { cash: 'cash', transfer: 'transfer', card: 'payoo', ewallet: 'vietqr', prepaid: 'prepaid' };

/** Chỉ còn một cơ sở; dữ liệu mẫu POS còn các chi nhánh cũ nên chỉ nhận đơn Q1 */
export const POS_BRANCH = 'q1';

export interface PosData { orders: Order[]; transactions: Transaction[] }

/** Tìm theo mã trước; đơn không có mã mới dò theo tên */
const serviceOf = (services: Service[], code: string, name: string) =>
  (code && services.find(x => x.code === code)) || services.find(x => x.name === name);

/** Mã mới từ POS chưa có trong danh mục kế toán: thêm vào, chưa đăng ký và chưa có thuế suất nên hóa đơn sẽ bị chặn */
function ensureServices(services: Service[], order: Order): Service[] {
  let out = services;
  order.lines.forEach(l => {
    if (serviceOf(out, l.code, l.name)) return;
    const type = l.type === 'package' || /^GOI/i.test(l.code) ? 'package' : l.type === 'product' || /^SP/i.test(l.code) ? 'product' : 'service';
    out = [...out, {
      id: l.code || `pos-${l.itemId}`, code: l.code || l.itemId, name: l.name, type, group: 'Chưa phân nhóm',
      taxClass: type === 'product' ? 'retail' : 'cosmetic', registeredName: '', price: l.listPrice,
    }];
  });
  return out;
}

const paymentsOf = (txns: Transaction[], orderDate: string): Payment[] => txns.filter(t => t.kind !== 'refund').map(t => ({
  id: `p-${t.id}`, method: METHOD[t.method], amount: t.amount, ref: t.txnRef || '', ...(ymd(new Date(t.at).getTime()) !== orderDate ? { date: ymd(new Date(t.at).getTime()) } : {}),
}));
const refundsOf = (txns: Transaction[]): BillRefund[] => txns.filter(t => t.kind === 'refund').map(t => ({
  id: `r-${t.id}`, at: new Date(t.at).getTime(), date: ymd(new Date(t.at).getTime()), amount: Math.abs(t.amount), method: METHOD[t.method], ref: t.txnRef || '', reason: t.refundId,
}));

/** Đơn POS thành bill chưa đánh số: tiền từng dòng là tiền sau chiết khấu, phần làm tròn đơn cộng vào dòng cuối */
export function billFromOrder(order: Order, txns: Transaction[], services: Service[], source: BillSource): Omit<Bill, 'seq' | 'no'> {
  const at = new Date(order.createdAt).getTime();
  const date = ymd(at);
  const lines = order.lines.map((l, i) => ({
    id: `l-${order.id}-${i}`, serviceId: serviceOf(services, l.code, l.name)?.id || l.itemId, name: serviceOf(services, l.code, l.name)?.name || l.name, gross: l.net,
    list: l.listPrice * l.qty, lineDiscount: l.promoAmount + l.lineDiscountAmount, orderDiscount: l.allocated,
  }));
  const ktv = order.lines.map(l => l.staffName || staffName(l.staffId)).find(Boolean);
  const diff = order.totals.total - lines.reduce((t, l) => t + l.gross, 0);
  if (lines.length && diff) lines[lines.length - 1].gross += diff;
  const cancelled = order.status === 'cancelled';
  const pkg = order.lines.length > 0 && order.lines.every(l => l.type === 'package' || /^GOI/i.test(l.code));
  return {
    id: `pos-${order.id}`, date, at, customer: order.customerName, phone: order.customerPhone || '', branchId: order.branchId || POS_BRANCH, source, posOrderId: order.id,
    debt: cancelled ? 0 : order.debt, refunds: refundsOf(txns), kind: pkg ? 'card' : 'service', lines, payments: paymentsOf(txns, date),
    status: cancelled ? 'cancelled' : 'valid', createdBy: order.cashier || 'POS',
    log: [{ at, by: order.cashier || 'POS', action: `Nhận từ đơn POS ${order.id}` }, ...(cancelled ? [{ at, by: 'POS', action: 'Hủy bill', reason: order.cancelReason || 'Đơn đã hủy trên POS' }] : [])],
    invoice: { status: 'none' },
    // POS chưa có trường Sale, CSKH: tạm lấy thu ngân làm Sale; Kế toán gán lại trước khi tính hoa hồng
    sale: order.cashier || undefined,
    team: ktv ? { ktv } : undefined,
  };
}

/** Nhận thay đổi của đơn đã có bill: thu nợ, hoàn tiền, hủy đơn */
function applyOrderChange(s: KtState, b: Bill, order: Order, txns: Transaction[], now: number): { bill: Bill; alert?: string } {
  const by = 'POS';
  let bill = b;
  const log = (action: string, reason?: string) => { bill = { ...bill, log: [...bill.log, { at: now, by, action, reason }] }; };

  const newPays = paymentsOf(txns, b.date).filter(p => !b.payments.some(x => x.id === p.id));
  if (newPays.length) {
    bill = { ...bill, payments: [...bill.payments, ...newPays], debt: order.debt };
    newPays.forEach(p => log(`Thu thêm ${fmtMoney(p.amount)} từ POS`));
  }
  const newRefunds = refundsOf(txns).filter(r => !b.refunds.some(x => x.id === r.id));
  if (newRefunds.length) {
    const total = newRefunds.reduce((t, r) => t + r.amount, 0);
    const why = `Hoàn ${fmtMoney(total)} trên POS`;
    bill = { ...bill, refunds: [...bill.refunds, ...newRefunds], invoice: flagInvoice(bill.invoice, why) };
    log(why);
  }
  if (order.status === 'cancelled' && b.status === 'valid') {
    if (s.locks[b.date]) return { bill, alert: `Đơn ${order.id} bị hủy trên POS sau khi sổ ngày ${b.date} đã khóa. Bill ${b.no} giữ nguyên, cần Kế toán trưởng xử lý.` };
    bill = applyCancel(bill, by, order.cancelReason || 'Đơn đã hủy trên POS', now);
  }
  return { bill };
}

export interface SyncResult { state: KtState; added: number; changed: number }

/** Đồng bộ một lô đơn POS vào state; chạy lại nhiều lần không sinh trùng */
export function syncOrders(s0: KtState, pos: PosData, source: BillSource, now: number): SyncResult {
  let s = s0;
  let added = 0; let changed = 0;
  const txnsOf = (id: string) => pos.transactions.filter(t => t.orderId === id);
  const orders = pos.orders
    .filter(o => source === 'server' || o.branchId === POS_BRANCH)
    .slice().sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  const alert = (orderId: string, text: string) => {
    const id = `${orderId}:${text}`;
    if (!s.posAlerts.some(a => a.id === id)) s = { ...s, posAlerts: [{ id, at: now, orderId, text }, ...s.posAlerts] };
  };

  orders.forEach(order => {
    const billId = `pos-${order.id}`;
    const found = s.bills.find(b => b.id === billId);
    if (found) {
      const { bill, alert: msg } = applyOrderChange(s, found, order, txnsOf(order.id), now);
      if (msg) alert(order.id, msg);
      if (bill !== found) { s = { ...s, bills: s.bills.map(b => (b.id === billId ? bill : b)) }; changed += 1; }
      return;
    }
    const date = ymd(new Date(order.createdAt).getTime());
    // Không chèn bill lùi vào ngày đã khóa
    if (s.locks[date]) { alert(order.id, `Đơn ${order.id} ngày ${date} (${fmtMoney(order.totals.total)}) chưa vào sổ vì ngày đó đã khóa.`); return; }
    const services = ensureServices(s.services, order);
    const seq = Math.max(0, ...s.bills.map(b => b.seq)) + 1;
    const bill: Bill = { ...billFromOrder(order, txnsOf(order.id), services, source), seq, no: billNo(seq) };
    s = { ...s, services, bills: [...s.bills, bill] };
    added += 1;
  });
  return { state: s, added, changed };
}
